import * as React from 'react';
import { get } from 'lodash';
import { produce } from 'immer';
import getMetricsData from '@api/metricContainer';
import instance from '@api/axiosInstance';
import { getInvertedPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import { createQuery } from '@src/components/widget-panel/utils/query';
import useWidgetQuery from '@src/components/widget-panel/hooks/useWidgetQuery';
import type { AppFlowMetadata } from '@src/hooks/flow/useFlowConfigs';
import { appendFlowIdentifier } from '@utils/filter';
import useQueryParams from '../../hooks/useQueryParams';
import { getFlowMetrics } from '@utils/flow/utils';

export type FlowTimeseriesDataResponse = Array<{
  totals: Record<string, number>;
  data: Array<{
    timestamp: string;
    results: Record<string, number>;
  }>;
}>;

const FLOW_WIDGET_STATIC_METRICS = ['AppSubscribers', 'AppTotalEvents'];

function getAllowedMetrics(metrics: string[], supportedMetrics: Record<string, unknown>) {
  return Array.from(
    new Set(
      metrics.filter(
        (item) =>
          item in supportedMetrics ||
          getInvertedPercentageMetricByKey({ metricKey: item }) in supportedMetrics,
      ),
    ),
  );
}

function useFlowMetricsQuery(wid: string, flowMeta: AppFlowMetadata) {
  const { interval, filter, _rKey, allowedMetrics, liveMode } = useQueryParams();
  const metrics = getAllowedMetrics(getFlowMetrics(flowMeta), allowedMetrics);
  const queryFilter = appendFlowIdentifier(filter, flowMeta.identifier);
  const payload = createQuery(
    metrics,
    `${interval.from}/${interval.to}`,
    interval.granularity,
    queryFilter,
  );

  return useWidgetQuery({
    wid,
    queryKey: ['widget', 'flow', payload.queries, _rKey],
    queryFn: () =>
      getMetricsData(payload, {}, instance).then((response) => {
        const { data, totals, annotations } = get(response, ['data', '0'], {
          totals: {},
          data: [],
          annotations: [],
        });
        const formattedData = formatTimeseriesData({
          metrics,
          data,
          interval,
          annotations,
        });
        return { data: formattedData, totals };
      }),
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !liveMode && !!metrics.length,
      staleTime: 5 * 60 * 1000, // 5m
    },
  });
}

function useFlowStaticMetricsQuery(wid: string, flowConfig: AppFlowMetadata) {
  const { interval, filter, _rKey, allowedMetrics, liveMode } = useQueryParams();
  const metrics = getAllowedMetrics(FLOW_WIDGET_STATIC_METRICS, allowedMetrics);
  const queryFilter = produce(filter as any[][], (draft) => {
    const successEventName = flowConfig.definition.events.success_event?.name;
    const rule = {
      field: 'event_name',
      op: 'eq',
      value: successEventName,
    };

    if (!successEventName) return;

    if (Array.isArray(draft)) {
      draft.push([rule]);

      return;
    }

    return [[rule]];
  });
  const payload = createQuery(
    metrics,
    `${interval.from}/${interval.to}`,
    interval.granularity,
    queryFilter,
  );

  return useWidgetQuery({
    wid,
    queryKey: ['widget', 'flow', payload.queries, _rKey],
    queryFn: () =>
      getMetricsData(payload, {}, instance).then((response) => {
        const { data, totals, annotations } = get(response, ['data', '0'], {
          totals: {},
          data: [],
          annotations: [],
        });
        const formattedData = formatTimeseriesData({
          metrics,
          data,
          interval,
          annotations,
        });
        return { data: formattedData, totals };
      }),
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !liveMode && !!metrics.length,
      staleTime: 5 * 60 * 1000, // 5m
    },
  });
}

export function useFlowQuery(wid: string, flowMeta: AppFlowMetadata) {
  const { refetch: flowRefetch, data: flowData, ...query } = useFlowMetricsQuery(wid, flowMeta);
  const {
    refetch: staticMetricsRefetch,
    data: staticMetricsData,
    ...staticMetricsQuery
  } = useFlowStaticMetricsQuery(wid, flowMeta);

  const refetch = React.useCallback(
    () => Promise.all([flowRefetch, staticMetricsRefetch]),
    [flowRefetch, staticMetricsRefetch],
  );

  return {
    flowData: flowData as FlowTimeseriesDataResponse,
    staticMetricsData: staticMetricsData as FlowTimeseriesDataResponse,
    isFetching: query.isFetching || staticMetricsQuery.isFetching,
    isError: query.isError || staticMetricsQuery.isError,
    queued: query.queued || staticMetricsQuery.queued,
    refetch,
  };
}
